// ======================================================================
// File Name       : EnterPriseUtils
//
// Copyright(C) 2017 NIPPON TELEGRAPH AND TELEPHONE EAST CORPORATION All
// Rights Reserved.
//
// This source code or any portion thereof must not be
// reproduced or used in any manner whatsoever.
// ======================================================================

/**
 * Convert data to node string
 *
 * @param {Object} data
 * @param {Array} enterpriseList
 * retirn @param {String} nodeStr
 */
import moment from 'moment';
import * as Path from '../constants/PathConstants';
import { DEFAULT_YEAR_MONTH, DEFAULT_DATE_SHORT_FORMAT_SERVER, USAGE_SETTING } from '../constants/index';
import { SELECT_BOX_DATA } from '../constants/SelectBoxData';
import { BILLING_SYSTEM_REGISTER_FLAG_STATUS, LIVE_INIT_COST_STATUS, CREDIT_CHECKED_FLAG_STATUS, PAYMENT_TYPE } from '../constants/EnterpriseConstants';
import { SUPERUSER_TYPE } from '../constants/SuperUserContants';

export function getEnterprisePath(data, enterpriseList) {
  if (!data || !enterpriseList || enterpriseList.length <= 0) return null;
  let nodeStr = '';
  const node = (child) => {
    const parentId = child ? child.parentEnterpriseInfo.parentEnterpriseCd : '';
    enterpriseList.reduce((children, enterprise) => {
      if (enterprise.enterpriseCd === parentId) {
        nodeStr = `${enterprise.enterpriseName} > ${nodeStr}`;
        if (enterprise.parentEnterpriseInfo.parentEnterpriseCd !==
          enterprise.enterpriseCd) {
          node(enterprise);
        }
      }
      return nodeStr;
    }, []);
  };
  for (const enterprise of enterpriseList) {
    if (data.enterpriseCd === enterprise.enterpriseCd) {
      nodeStr = enterprise.enterpriseName;
      if (enterprise.parentEnterpriseInfo.parentEnterpriseCd !== enterprise.enterpriseCd) {
        node(enterprise);
      }
    }
  }

  return nodeStr;
}
export function getSystemUsageMonthRange(range) {
  const targetMonths = [];
  let i = 0;
  for (i; i <= range; i++) {
    const month = moment().add(i, 'month');
    const monthTemp = { name: month.format(DEFAULT_YEAR_MONTH), value: month.format(DEFAULT_DATE_SHORT_FORMAT_SERVER) };
    targetMonths.push(monthTemp);
  }
  return targetMonths;
}

export function convertListToObjectByEnterpriseCd(enterpriseList) {
  return enterpriseList.reduce((acc, cur) => {
    const { enterpriseCd } = cur;
    acc[enterpriseCd] = cur;
    return acc;
  }, {});
}

export function setEnterpriseRelation(enterprises, enterpriseCd, parentEnterpriseCd) {
  const setNewChild = (targetSecernId, childSecernId) => {
    const target = enterprises.bySecernId[targetSecernId];

    if (target) {
      let child = (childSecernId && [...(target.child || []), childSecernId]) || [];
      child = [...new Set(child)]; // #7057
      enterprises.bySecernId[targetSecernId] = { ...target, child };
    } else {
      // target enterprise is "current" enterprise which not return from server as default
      // so move direct-child-enterprise to root node list
      enterprises.allSecernIds.push(childSecernId);
    }
  };

  parentEnterpriseCd && (enterpriseCd !== parentEnterpriseCd)
    ? setNewChild(parentEnterpriseCd, enterpriseCd)
    : enterprises.allSecernIds.push(enterpriseCd);
}

export function getEnterprisePlanName(pricePlan) {
  const check = parseInt(pricePlan, 10);
  if (isNaN(check)) {
    return '';
  }
  const index = SELECT_BOX_DATA.ENTERPRISE_PRICE_PLAN.findIndex(format => format.value === check);
  if (index < 0) {
    return '';
  }
  return SELECT_BOX_DATA.ENTERPRISE_PRICE_PLAN[index].name || '';
}

// sprint_20230403 #14 START
export function getNextMonthForConfirmCancelEnterprise() {
  return moment().add(1, 'months').endOf('date').format('YYYY年MM月');
}
// sprint_20230403 #14 END

// sprint_20230605_tma START
export function getCurrentMonthForNotifyCancelEnterprise() {
  return moment().endOf('date').format('YYYY年MM月')
}
// sprint_20230605_tma END

// sprint_20230403 #4 START
export function getEnterpriseDeleteFlagLabel(value) {
  return value ? '解約' : '利用中';
}

export function getBillingSystemTargetLabel(value) {
  return value === USAGE_SETTING.USE ? 'Stripe' : '社内システム';
}

export function getCreditCheckedFlagLabel(value) {
  // sprint_20230508_tma #20 START
  switch (value) {
    case CREDIT_CHECKED_FLAG_STATUS.UN_CONFIRMED:
      return '未確認';
    case CREDIT_CHECKED_FLAG_STATUS.NO_PROBLEM:
      return '問題なし';
    case CREDIT_CHECKED_FLAG_STATUS.PROBLEM:
      return '問題あり';
    default:
      return '';
  }
  // sprint_20230508_tma #20 END
}

export function getServiceStopFlagLabel(value) {
  return value ? '利用停止' : '利用可能';
}

export function getBillingSystemRegisterFlagLabel(status) {
  switch (status) {
    case BILLING_SYSTEM_REGISTER_FLAG_STATUS.NOT_REGISTERED:
      return '未登録';
    case BILLING_SYSTEM_REGISTER_FLAG_STATUS.PENDING_REGISTERED:
      return '登録待ち';
    case BILLING_SYSTEM_REGISTER_FLAG_STATUS.REGISTERED:
      return '登録済み';
    default:
      return '';
  }
}

export function getLiveInititalCostStatus(status) {
  switch (status) {
    case LIVE_INIT_COST_STATUS.NOT_BILLED:
      return '未請求';
    case LIVE_INIT_COST_STATUS.PENDING_BILLING:
      return '請求処理待ち';
    case LIVE_INIT_COST_STATUS.BILLED:
      return '請求済み';
    default:
      return '';
  }
}
// sprint_20230403 #4 END

// sprint_20230605_tma START
export function getPaymentType(value) {
  switch (value) {
    case PAYMENT_TYPE.CREDIT_CARD:
      return 'カード支払い';
    case PAYMENT_TYPE.BANK_TRANSFER:
      return '銀行振込';
    case PAYMENT_TYPE.OPS:
      return 'OpS';
    default:
      return '';
  }
}
// sprint_20230605_tma END

// Step10.0.0 START
export function getSuperUserType(rootFlag) {
  return rootFlag ? SUPERUSER_TYPE.rootUser : SUPERUSER_TYPE.normalUser;
}
// Step10.0.0 END

// STEP12.0.0 TAS-18710 START
export function getManualPasswordSettingFlag(value) {
  return value ? '有効' : '無効';
}
// STEP12.0.0 TAS-18710 END